import { Directive, EventEmitter, HostBinding, HostListener, Output } from '@angular/core';

/**
 * File drop zone directive with visual active state and normalized file list output.
 *
 * Outputs:
 * - `filesDropped: File[]` — Emits a list of dropped files.
 *
 * Example:
 * ```html
 * <div drop-zone (filesDropped)="upload($event)">Drop files here</div>
 * ```
 */
@Directive({
    selector: '[drop-zone]',
    standalone: true,
})
export class DropZoneDirective {
    @Output()
    /**
     * Emits normalized list of dropped files.
     */
    filesDropped = new EventEmitter<Array<File>>();

    @HostBinding('class.drop-zone-active')
    /**
     * True while a drag operation is over the host.
     */
    active = false;

    @HostListener('drop', ['$event'])
    /**
     * Handles drop event, extracts files from DataTransfer and emits them.
     */
    onDrop(e: DragEvent) {
        e.preventDefault();
        this.active = false;

        const { dataTransfer } = e;
        if (dataTransfer.items) {
            const files = [];
            for (let i = 0; i < dataTransfer.items.length; i++) {
                if (dataTransfer.items[i].kind === 'file') {
                    files.push(dataTransfer.items[i].getAsFile());
                }
            }
            dataTransfer.items.clear();
            this.filesDropped.emit(files);
        } else {
            const files = dataTransfer.files;
            dataTransfer.clearData();
            this.filesDropped.emit(Array.from(files));
        }
    }

    @HostListener('dragover', ['$event'])
    /**
     * Prevents default to allow drop and marks zone as active.
     */
    onDragOver(e: DragEvent) {
        e.stopPropagation();
        e.preventDefault();
        this.active = true;
    }

    @HostListener('dragleave', ['$event'])
    /**
     * Resets active state when drag leaves the host.
     */
    onDragLeave(e: DragEvent) {
        this.active = false;
    }

    @HostListener('body:dragover', ['$event'])
    /**
     * Prevents browser from opening files when dragging over the page.
     */
    onBodyDragOver(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    @HostListener('body:drop', ['$event'])
    /**
     * Prevents browser default drop behavior on the page.
     */
    onBodyDrop(e: DragEvent) {
        e.preventDefault();
    }
}
